import { frameId, type FrameRect, type ManifestAppearanceState, type ManifestObjectAppearance, type ObjectTypeDefinition } from "daedalus-core";
import { distanceSquared, faces, outranks } from "./interact-choice.ts";
import { AUTHORING_CELL, EntityKind, FOOT_HALF_WIDTH, FOOT_HEIGHT, SIXTEENTHS, inActivation, inReach, isServerVerb, offersNoVerb, objectTypeOfKind, SHRINE, TALK_VERB, TRAVEL_VERB, WAYPOINT, type ActivationCells, type EntityState, type Facing, type Reacher } from "odyssey-shared";

/**
 * The thing E acts on and the interaction prompt names (P1.9-T17; ADR-051, ADR-053 decision 8, ADR-058): the first
 * thing in the client's view that offers a verb in its state and is in range of the predicted local character,
 * by the activation cells its state carries, or by the facing rule where it carries none, the test the map server
 * applies, so the key answers where the reply will. The verb is the first transition the thing's type declares leaving
 * its state (`verbFor`), from the version's contract. A type driven by presence, such as the Roof, offers no verb: its
 * verbs never travel, and it has no entity kind, so it never reaches the view either. With two usable things in
 * reach, the one the character faces wins, else the nearest to its feet, ties by handle (`interact-choice.ts`,
 * P1.9-T29), so the choice depends on nothing the network orders. An NPC (kind 11, ADR-136 decision 5.1) offers
 * `talk` by the facing rule, as the map server reaches it, and its bracket spans a tile across and its art's height
 * above its feet (`watchNpcHeads`) with half a tile more, so the bracket takes in the nameplate over its head and the
 * prompt stands clear of it; the page cannot tell whether its placement names a conversation, so every NPC
 * offers it, and one that names none answers that nothing happens. A thing whose type declares `uninterruptible`
 * offers no verb while the page is still playing the strip of its last change (ADR-058's amendment of 2026-10-07): the map server refuses every verb
 * on it until that strip has run, so the prompt stands down for the move and E passes it over. When such a thing is in
 * reach and nothing else is found, the target says so (`waiting`), so E stays silent rather than saying there is
 * nothing to use. The lookup is told who knows that with `watchMoves`, and is invalidated when such a strip ends.
 *
 * E and the prompt share one lookup, so the prompt names exactly what E would act on. It writes its answer into one
 * target object it keeps, and rescans the view only when the cells the predicted feet box covers change, or when the
 * things in view change (the scene says so with `invalidateTarget` whenever a snapshot changes a thing, its state or
 * the view's order of things). While a thing reached by the facing rule is in view the predicted position and facing
 * themselves are the key, since that rule is not a matter of cells. A frame that changes none of these compares four
 * cells and returns the same object, so the prompt allocates nothing per frame; a rescan visits the view through a
 * callback made once and writes into objects made once. Each thing's art box, which the prompt's bracket marks, is
 * measured once per appearance and state from the atlas's frame rects when the world loads.
 *
 * @example
 * ```ts
 * const lookup = createTargetLookup({ appearances, types, frames, tileSize: 32 });
 * const target = resolveTarget(lookup, predictor.predicted, connection.view.entities);
 * if (target.found) {
 *   connection.sendInteract(target.handle, target.verb);
 * }
 * ```
 */

/**
 * What E would act on: whether anything is in reach, its handle and the verb its state offers, and the box of its
 * art in world pixels, which the prompt's bracket marks (left and top inclusive, right and bottom exclusive).
 * `waiting` is true when nothing was found and a thing in reach is finishing its move, so there is something here
 * that takes no key yet; it is false whenever something is found.
 */
export interface InteractTarget {
  readonly found: boolean;
  readonly waiting: boolean;
  readonly handle: number;
  readonly verb: string;
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/**
 * Nothing in reach.
 */
export const NO_TARGET: InteractTarget = Object.freeze({ found: false, waiting: false, handle: -1, verb: "", left: 0, top: 0, right: 0, bottom: 0 });

/**
 * What the lookup reads of a loaded world version: its appearance table, its object types by id from the contract,
 * the asset manifest's frame rects, and its tile size in pixels.
 */
export interface TargetWorld {
  readonly appearances: readonly ManifestObjectAppearance[];
  readonly types: ReadonlyMap<string, ObjectTypeDefinition>;
  readonly frames: Readonly<Record<string, FrameRect>>;
  readonly tileSize: number;
}

/**
 * A lookup: the target it last found, which is the object `resolveTarget` returns every time, and how many times it
 * has scanned the view.
 */
export interface TargetLookup {
  readonly target: InteractTarget;
  readonly scans: number;
}

/**
 * A state's art box relative to its thing's feet point, in world pixels.
 */
interface ArtBox {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/**
 * The target, written in place.
 */
interface MutableTarget {
  found: boolean;
  waiting: boolean;
  handle: number;
  verb: string;
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * Activation cells written in place for each thing a scan tests.
 */
interface MutableActivation {
  x: number;
  y: number;
  width: number;
  flipX: boolean;
  cells: ActivationCells["cells"];
  offset: ActivationCells["offset"];
}

/**
 * Everything a lookup keeps: the world, the art boxes by appearance and state, the target and the activation cells written in
 * place, the key of the last scan (the feet box's cells, and the position and facing while a thing reached by the
 * facing rule is in view), the position the scan in progress tests, the visitor made once, and what says whether a
 * thing's strip is still playing.
 */
interface LookupState {
  readonly world: TargetWorld;
  readonly boxes: readonly (readonly ArtBox[])[];
  readonly target: MutableTarget;
  readonly activation: MutableActivation;
  readonly visit: (thing: EntityState) => void;
  candidates: number;
  bestFaced: boolean;
  bestDistance: number;
  moving: (handle: number) => boolean;
  npcHead: (appearance: number) => number;
  scans: number;
  stale: boolean;
  exact: boolean;
  cellLeft: number;
  cellRight: number;
  cellTop: number;
  cellBottom: number;
  x: number;
  y: number;
  facing: Facing;
  self: Reacher;
}

/**
 * The box of a frame a thing's feet point sits under, for a frame the atlas does not cut: the feet point itself.
 */
const AT_THE_FEET: ArtBox = Object.freeze({ left: 0, top: 0, right: 0, bottom: 0 });

/**
 * The art box of one state of an appearance, relative to the feet point: the frame's opaque bounds, mirrored with the
 * appearance's flips, on a frame drawn with its bottom on the feet line and centred on the feet across, as the scene
 * anchors a thing (`objectAnchor`). A frame with no measured bounds stands in whole down to its base.
 *
 * @param look The appearance.
 * @param state One of its states.
 * @param frames The asset manifest's frame rects.
 * @returns The box, or the feet point for a frame the atlas does not cut.
 */
function artBox(look: ManifestObjectAppearance, state: ManifestAppearanceState, frames: Readonly<Record<string, FrameRect>>): ArtBox {
  const frame = frames[frameId(state.frame.tileset, null, state.frame.tile)];
  if (frame === undefined) {
    return AT_THE_FEET;
  }
  const art = frame.art ?? { x: 0, y: 0, w: frame.w };
  const x = look.flipX ? frame.w - art.x - art.w : art.x;
  const top = look.flipY ? frame.h - frame.base : art.y;
  const bottom = look.flipY ? frame.h - art.y : frame.base;
  return { left: x - frame.w / 2, top: top - frame.h, right: x + art.w - frame.w / 2, bottom: bottom - frame.h };
}

/**
 * What a lookup asks of a thing's move until `watchMoves` names who knows: nothing moves.
 *
 * @returns False.
 */
function neverMoving(): boolean {
  return false;
}

/**
 * How far an NPC's art reaches above its feet until `watchNpcHeads` names who knows: one tile.
 *
 * @returns 0, which `considerThing` reads as one tile.
 */
function oneTileHead(): number {
  return 0;
}

/**
 * Makes a lookup for a loaded world version, measuring every appearance's art boxes once.
 *
 * @param world The world's appearances, object types, frame rects and tile size.
 * @returns The lookup, stale until its first resolve.
 */
export function createTargetLookup(world: TargetWorld): TargetLookup {
  const state: LookupState = {
    world,
    boxes: world.appearances.map((look) => look.states.map((each) => artBox(look, each, world.frames))),
    target: { found: false, waiting: false, handle: -1, verb: "", left: 0, top: 0, right: 0, bottom: 0 },
    activation: { x: 0, y: 0, width: 0, flipX: false, cells: [], offset: undefined },
    visit: (thing) => considerThing(state, thing),
    candidates: 0,
    bestFaced: false,
    bestDistance: 0,
    moving: neverMoving,
    npcHead: oneTileHead,
    scans: 0,
    stale: true,
    exact: false,
    cellLeft: 0,
    cellRight: 0,
    cellTop: 0,
    cellBottom: 0,
    x: 0,
    y: 0,
    facing: 0,
    self: { x: 0, y: 0, facing: 0 },
  };
  return state;
}

/**
 * Tells a lookup the things in view changed (a thing appeared, left, moved, changed state or took another place in
 * the view's order), so its next resolve scans the view again.
 *
 * @param lookup The lookup.
 */
export function invalidateTarget(lookup: TargetLookup): void {
  (lookup as LookupState).stale = true;
}

/**
 * Tells a lookup how to ask whether a thing's transition strip is still playing on this page, which decides whether
 * an uninterruptible thing offers its verb. Whoever answers also invalidates the lookup when such a strip ends, since
 * nothing else changes then.
 *
 * @param lookup The lookup.
 * @param moving Whether the thing with a handle is still playing its strip; asked only during a scan, of things whose
 *   type declares `uninterruptible`.
 */
export function watchMoves(lookup: TargetLookup, moving: (handle: number) => boolean): void {
  (lookup as LookupState).moving = moving;
}

/**
 * Tells a lookup how far an NPC's art reaches above its feet, which sets the top of the bracket the prompt draws
 * around it.
 *
 * @param lookup The lookup.
 * @param head The height in world pixels for an NPC's appearance; 0 or less stands for one tile.
 */
export function watchNpcHeads(lookup: TargetLookup, head: (appearance: number) => number): void {
  (lookup as LookupState).npcHead = head;
}

/**
 * The thing E acts on for the predicted local character: the target of the last scan when neither the cells the
 * feet box covers nor the things in view changed since, else a new scan's. The same object every call.
 *
 * @param lookup The lookup.
 * @param self The predicted local character's feet position in sixteenths and its facing.
 * @param entities The client's view: every entity it holds by handle, in its order.
 * @returns The target, written in place.
 */
export function resolveTarget(lookup: TargetLookup, self: Reacher, entities: ReadonlyMap<number, EntityState>): InteractTarget {
  const state = lookup as LookupState;
  const left = Math.floor((self.x - FOOT_HALF_WIDTH) / AUTHORING_CELL);
  const right = Math.floor((self.x + FOOT_HALF_WIDTH - 1) / AUTHORING_CELL);
  const top = Math.floor((self.y - FOOT_HEIGHT) / AUTHORING_CELL);
  const bottom = Math.floor((self.y - 1) / AUTHORING_CELL);
  if (!state.stale && left === state.cellLeft && right === state.cellRight && top === state.cellTop && bottom === state.cellBottom && (!state.exact || (self.x === state.x && self.y === state.y && self.facing === state.facing))) {
    return state.target;
  }
  state.stale = false;
  state.cellLeft = left;
  state.cellRight = right;
  state.cellTop = top;
  state.cellBottom = bottom;
  state.x = self.x;
  state.y = self.y;
  state.facing = self.facing;
  scanThings(state, self, entities);
  return state.target;
}

/**
 * Whether one thing the view holds still reaches the predicted local character, by the rule E uses (its activation
 * cells where its state carries them, else the facing rule), whatever else is in reach and whatever E would act on.
 * The reading panel asks this each frame while it shows the thing's text (P1.9-T37). It neither scans the view nor
 * touches the lookup's answer or its cache, and allocates nothing of its own: it reads one record and tests it through the activation cells
 * the lookup writes in place.
 *
 * @param lookup The lookup.
 * @param handle The thing's handle.
 * @param self The predicted local character's feet position in sixteenths and its facing.
 * @param entities The client's view.
 * @returns True when the thing is in the view, is not a player and reaches the character.
 */
export function thingReaches(lookup: TargetLookup, handle: number, self: Reacher, entities: ReadonlyMap<number, EntityState>): boolean {
  const state = lookup as LookupState;
  const thing = entities.get(handle);
  if (thing === undefined || thing.kind === EntityKind.Player) {
    return false;
  }
  const look = state.world.appearances[thing.appearance];
  const current = look?.states[look.states[thing.anim] === undefined ? look.initial : thing.anim];
  if (look === undefined || current === undefined) {
    return false;
  }
  if (current.activation.length === 0) {
    return inReach(self, thing);
  }
  const activation = state.activation;
  activation.x = thing.x;
  activation.y = thing.y;
  activation.width = current.width;
  activation.flipX = look.flipX;
  activation.cells = current.activation;
  activation.offset = look.offset;
  return inActivation(self, activation, state.world.tileSize);
}

/**
 * Scans the view for the thing in reach that offers a verb and ranks first (`outranks`), writing it into the target.
 * With two or more such things the choice turns on the character's facing and exact position, so the lookup keys on them.
 *
 * @param state The lookup.
 * @param self The predicted local character.
 * @param entities The view.
 */
function scanThings(state: LookupState, self: Reacher, entities: ReadonlyMap<number, EntityState>): void {
  state.scans += 1;
  state.exact = false;
  state.candidates = 0;
  state.bestFaced = false;
  state.bestDistance = 0;
  state.self = self;
  const target = state.target;
  target.found = false;
  target.waiting = false;
  target.handle = -1;
  target.verb = "";
  target.left = 0;
  target.top = 0;
  target.right = 0;
  target.bottom = 0;
  entities.forEach(state.visit);
  state.exact = state.exact || state.candidates > 1;
}

/**
 * Considers one entity of the view as the target: a thing whose type the contract declares, is not driven by presence,
 * is not an uninterruptible thing in the middle of its move, offers a verb in its state and reaches the predicted
 * local character, and that outranks any found before it: faced over not faced, then nearer, then the lower handle.
 *
 * @param state The lookup.
 * @param thing The entity.
 */
function considerThing(state: LookupState, thing: EntityState): void {
  const target = state.target;
  if (thing.kind === EntityKind.Player) {
    return;
  }
  if (thing.kind === EntityKind.Npc) {
    considerNpc(state, thing);
    return;
  }
  const typeId = objectTypeOfKind(thing.kind);
  const type = typeId === undefined ? undefined : state.world.types.get(typeId);
  const look = state.world.appearances[thing.appearance];
  if (type === undefined || type.presence !== undefined || look === undefined) {
    return;
  }
  const index = look.states[thing.anim] === undefined ? look.initial : thing.anim;
  const current = look.states[index];
  if (current === undefined) {
    return;
  }
  if (type.uninterruptible === true && state.moving(thing.handle)) {
    target.waiting = !target.found && (target.waiting || reaches(state, thing, current, look));
    return;
  }
  const verb = offeredVerb(type, current);
  if (verb === "" || !reaches(state, thing, current, look)) {
    return;
  }
  state.candidates += 1;
  const dx = thing.x - state.self.x;
  const dy = thing.y - state.self.y;
  const faced = faces(state.self.facing, dx, dy);
  const distance = distanceSquared(dx, dy);
  if (target.found && !outranks(faced, distance, thing.handle, state.bestFaced, state.bestDistance, target.handle)) {
    return;
  }
  state.bestFaced = faced;
  state.bestDistance = distance;
  const box = state.boxes[thing.appearance]?.[index] ?? AT_THE_FEET;
  const feetX = (thing.x * state.world.tileSize) / SIXTEENTHS;
  const feetY = (thing.y * state.world.tileSize) / SIXTEENTHS;
  target.found = true;
  target.waiting = false;
  target.handle = thing.handle;
  target.verb = verb;
  target.left = feetX + box.left;
  target.top = feetY + box.top;
  target.right = feetX + box.right;
  target.bottom = feetY + box.bottom;
}

/**
 * Considers an NPC as the target: it offers `talk` when it reaches the predicted local character by the facing rule,
 * and outranks any found before it as a thing does.
 *
 * @param state The lookup.
 * @param thing The NPC.
 */
function considerNpc(state: LookupState, thing: EntityState): void {
  state.exact = true;
  if (!inReach(state.self, thing)) {
    return;
  }
  state.candidates += 1;
  const target = state.target;
  const dx = thing.x - state.self.x;
  const dy = thing.y - state.self.y;
  const faced = faces(state.self.facing, dx, dy);
  const distance = distanceSquared(dx, dy);
  if (target.found && !outranks(faced, distance, thing.handle, state.bestFaced, state.bestDistance, target.handle)) {
    return;
  }
  state.bestFaced = faced;
  state.bestDistance = distance;
  const tile = state.world.tileSize;
  const head = state.npcHead(thing.appearance);
  const feetX = (thing.x * tile) / SIXTEENTHS;
  const feetY = (thing.y * tile) / SIXTEENTHS;
  target.found = true;
  target.waiting = false;
  target.handle = thing.handle;
  target.verb = TALK_VERB;
  target.left = feetX - tile / 2;
  target.top = feetY - (head > 0 ? head : tile) - tile / 2;
  target.right = feetX + tile / 2;
  target.bottom = feetY;
}

/**
 * The verb a type offers in a state: its first transition leaving the state that is a player's to use, as `verbFor`
 * reads it (a verb the map server keeps, a chest's `shut`, is passed over: `isServerVerb`), and none on a type
 * that offers none (`offersNoVerb`: the training dummy, ADR-058's amendment for P2.2-T7). A shrine or a waypoint
 * declares no transition a player uses (ADR-096) but offers `travel` from a state that carries activation cells, the
 * only place the map server answers it (ADR-137 decision 6).
 *
 * @param type The object type.
 * @param current The state.
 * @returns The verb, or an empty string when the state offers none.
 */
function offeredVerb(type: ObjectTypeDefinition, current: ManifestAppearanceState): string {
  if (type.type === SHRINE || type.type === WAYPOINT) {
    return current.activation.length > 0 ? TRAVEL_VERB : "";
  }
  if (offersNoVerb(type)) {
    return "";
  }
  const stateId = current.id;
  const transitions = type.transitions;
  for (let index = 0; index < transitions.length; index += 1) {
    const transition = transitions[index] as ObjectTypeDefinition["transitions"][number];
    if (transition.from === stateId && !isServerVerb(type, transition.verb)) {
      return transition.verb;
    }
  }
  return "";
}

/**
 * Whether a thing is in range of the scan's position: by the activation cells its state carries, else by the facing
 * rule, which makes the position and facing themselves the lookup's key until the next scan.
 *
 * @param state The lookup.
 * @param thing The thing.
 * @param current The state its record names.
 * @param look The thing's appearance: its mirroring lays its activation cells from its frame's right edge, and its pixel offset
 *   lays the activation cells from the frame as it is drawn (ADR-040 amendment of 2026-10-04, P1.6-T83).
 * @returns True when a verb reaches it.
 */
function reaches(state: LookupState, thing: EntityState, current: ManifestAppearanceState, look: ManifestObjectAppearance): boolean {
  if (current.activation.length > 0) {
    const activation = state.activation;
    activation.x = thing.x;
    activation.y = thing.y;
    activation.width = current.width;
    activation.flipX = look.flipX;
    activation.cells = current.activation;
    activation.offset = look.offset;
    return inActivation(state.self, activation, state.world.tileSize);
  }
  state.exact = true;
  return inReach(state.self, thing);
}
